import React, { useState } from "react";
import "./CostSupportInfo.css";

type TabKey = "kotsu" | "faq" | "shikumi";

const ChevronDown = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M4 6l4 4 4-4" />
  </svg>
);

type AccordionItemProps = {
  id: string;
  badge: React.ReactNode;
  question: string;
  openIds: Set<string>;
  onToggle: (id: string) => void;
  children: React.ReactNode;
};

function AccordionItem({ id, badge, question, openIds, onToggle, children }: AccordionItemProps) {
  const isOpen = openIds.has(id);
  return (
    <div className={`csup-item${isOpen ? " csup-open" : ""}`}>
      <div className="csup-iq" onClick={() => onToggle(id)}>
        {badge}
        <span className="csup-iq-text">{question}</span>
        <span className="csup-chev">
          <ChevronDown />
        </span>
      </div>
      <div className="csup-ia">
        <div className="csup-ia-inner">{children}</div>
      </div>
    </div>
  );
}

type Props = {
  sectionId?: string;
};

export function UserManagementSupportInfo({ sectionId = "support-section" }: Props) {
  const [activeTab, setActiveTab] = useState<TabKey>("kotsu");
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="csup-root" id={sectionId}>
      {/* セクションヘッダー */}
      <div className="csup-sec-header">
        <div className="csup-sec-icon">
          <svg viewBox="0 0 20 20">
            <path d="M10 2C5.6 2 2 5.6 2 10s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm1 13H9v-2h2v2zm0-4H9c0-3.25 3-3 3-5 0-1.1-.9-2-2-2s-2 .9-2 2H7c0-1.65 1.35-3 3-3s3 1.35 3 3c0 2-3 2.25-3 5z" />
          </svg>
        </div>
        <span className="csup-sec-title">ユーザー管理 サポート情報</span>
        <span className="csup-sec-date">最終更新：2026年4月</span>
      </div>

      {/* タブ */}
      <div className="csup-tabs">
        <button
          className={`csup-tab csup-tab-kotsu${activeTab === "kotsu" ? " csup-active" : ""}`}
          onClick={() => setActiveTab("kotsu")}
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M2 11h12v1.5a1 1 0 01-1 1H3a1 1 0 01-1-1V11zm6-9.5a5 5 0 015 5c0 1.8-1 3.4-2.4 4.3L10 12H6l-.6-1.2A5 5 0 018 1.5zm1 2.3V5h1.5v1.5H9V8H7.5V6.5H6V5h1.5V3.8h1.5z" />
          </svg>
          運用のコツ
        </button>
        <button
          className={`csup-tab csup-tab-faq${activeTab === "faq" ? " csup-active" : ""}`}
          onClick={() => setActiveTab("faq")}
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M14 2H2a1 1 0 00-1 1v8a1 1 0 001 1h2v2.5l3-2.5h7a1 1 0 001-1V3a1 1 0 00-1-1zM8 9.5a1 1 0 110-2 1 1 0 010 2zm1-3.3c0 .4-.2.7-.5.9L8 7.4V7h-.5V5.8l.8-.5c.2-.1.2-.2.2-.3 0-.3-.2-.5-.5-.5s-.5.2-.5.5H6c0-.9.7-1.5 1.5-1.5S9 5.3 9 6.2z" />
          </svg>
          よくある質問
        </button>
        <button
          className={`csup-tab csup-tab-shikumi${activeTab === "shikumi" ? " csup-active" : ""}`}
          onClick={() => setActiveTab("shikumi")}
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 11.5a1 1 0 110-2 1 1 0 010 2zm.7-4.2c-.4.3-.7.6-.7 1v.2H6.5v-.2c0-.9.5-1.6 1.1-2 .4-.3.6-.6.6-1 0-.6-.5-1-1.2-1-.6 0-1.1.4-1.2.9H4.3C4.5 4.8 5.6 4 7 4c1.6 0 2.7 1 2.7 2.3 0 .9-.5 1.6-1 2z" />
          </svg>
          仕組みを知る
        </button>
      </div>

      {/* 運用のコツ */}
      <div className={`csup-panel${activeTab === "kotsu" ? " csup-active" : ""}`}>
        <AccordionItem
          id="kotsu-1"
          badge={<span className="csup-badge csup-badge-kotsu">コツ</span>}
          question="メンバー全員分のアカウントを個人単位で作成しましょう"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>1つのアカウントをチームで共有するのではなく、メンバー1人につき1アカウントを作成することを推奨します。個人ごとのアクセス管理がしやすくなり、担当者変更時の対応もスムーズになります。</p>
            <p>なお、登録できるユーザー数は管理者を含めて3名までです。</p>
          </div>
          <div className="csup-note csup-note-green">
            ユーザー上限は3名です。追加が必要な場合はお問い合わせください。
          </div>
        </AccordionItem>

        <AccordionItem
          id="kotsu-2"
          badge={<span className="csup-badge csup-badge-kotsu">コツ</span>}
          question="退職・担当変更の際はすぐにアカウントを削除しましょう"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>担当者が変わった際は、旧担当者のアカウントを速やかに削除してください。アクセス権が残ったままになると、情報セキュリティのリスクにつながります。</p>
            <p>退職・異動時の社内対応フローに「利益Naviアカウントの削除」を組み込んでおくことをおすすめします。</p>
          </div>
          <div className="csup-note csup-note-amber">
            アカウント削除は「操作」列の「削除」ボタンから行えます。
          </div>
        </AccordionItem>

        <AccordionItem
          id="kotsu-3"
          badge={<span className="csup-badge csup-badge-kotsu">コツ</span>}
          question="招待はGoogleアカウントのメールアドレスで行います"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>ユーザーの招待はGmailまたはGoogle Workspaceなど、Googleアカウントでログイン可能なメールアドレスのみ対応しています。招待したいメンバーのGoogleアカウントのメールアドレスを事前に確認してから招待してください。</p>
          </div>
          <div className="csup-note csup-note-blue">
            GmailやGoogle Workspace以外のメールアドレスでは招待できません。
          </div>
        </AccordionItem>
      </div>

      {/* よくある質問 */}
      <div className={`csup-panel${activeTab === "faq" ? " csup-active" : ""}`}>
        <AccordionItem
          id="faq-1"
          badge={<span className="csup-badge csup-badge-faq">Q</span>}
          question="追加できるユーザー数に上限はありますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>管理者を含めて最大3名まで登録できます。3名を超える場合は既存のユーザーを削除してから新たに追加してください。ユーザー数の上限に関するご要望はお問い合わせフォームよりご連絡ください。</p>
          </div>
        </AccordionItem>

        <AccordionItem
          id="faq-2"
          badge={<span className="csup-badge csup-badge-faq">Q</span>}
          question="管理者とメンバーで見られる画面は違いますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>現時点では管理者とメンバーの表示内容に差はありません。今後のアップデートで、管理者は営業利益を含む全データを確認でき、メンバーは限界利益までの表示に制限される権限制限機能を追加予定です。</p>
          </div>
          <div className="csup-note csup-note-blue">
            権限制限機能は今後実装予定です。最新情報はお問い合わせください。
          </div>
        </AccordionItem>

        <AccordionItem
          id="faq-3"
          badge={<span className="csup-badge csup-badge-faq">Q</span>}
          question="削除したユーザーは元に戻せますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>削除したユーザーを復元することはできません。再度利用が必要な場合は、改めて招待してください。</p>
            <p>なお、ユーザーを削除してもそのユーザーが登録・編集したデータ（原価・固定費など）は削除されず、引き続き利用できます。</p>
          </div>
          <div className="csup-note csup-note-amber">
            ユーザー削除はデータには影響しません。
          </div>
        </AccordionItem>
      </div>

      {/* 仕組みを知る */}
      <div className={`csup-panel${activeTab === "shikumi" ? " csup-active" : ""}`}>
        <AccordionItem
          id="shikumi-1"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="ユーザー情報はどのように管理・保護されていますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>各ユーザーのパスワードは暗号化されて保存されており、運営側でも内容を確認することはできません。</p>
          </div>
        </AccordionItem>

        <AccordionItem
          id="shikumi-2"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="招待の仕組みはどのようになっていますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>管理者がメールアドレスを入力して「ユーザーを招待」ボタンを押すと、招待メールが送信されます。招待を受けたユーザーがメール内のリンクからGoogleアカウントでログインすることで、利益Naviへのアクセスが可能になります。招待はGoogleアカウント対応のメールアドレスのみ有効です。</p>
          </div>
        </AccordionItem>

        <AccordionItem
          id="shikumi-3"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="管理者とメンバーの権限の違いは何ですか？（今後実装予定）"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>現時点では管理者とメンバーの機能差はありませんが、今後のアップデートで権限制限機能を追加予定です。</p>
            <p>想定している仕様では、管理者は営業利益を含む全データを確認でき、メンバーは限界利益までの表示に制限される予定です。詳細が決まり次第お知らせします。</p>
          </div>
          <div className="csup-note csup-note-blue">
            権限制限機能は今後実装予定です。
          </div>
        </AccordionItem>

        <AccordionItem
          id="shikumi-4"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="ユーザーを削除するとデータはどうなりますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>ユーザーアカウントを削除しても、そのユーザーが登録・編集したデータ（原価・固定費など）は削除されません。データはショップ単位で管理されているため、担当者が変わってもこれまでの入力内容はそのまま引き継がれます。</p>
          </div>
        </AccordionItem>

        <AccordionItem
          id="shikumi-5"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="ユーザーの権限（管理者・メンバー）はあとから変更できますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>ユーザー一覧の「権限」列のドロップダウンから、登録後でも管理者・メンバーの変更が可能です。権限によって閲覧できるデータの範囲が変わる予定ですので、適切な権限を設定してください（権限制限機能は今後実装予定）。</p>
          </div>
        </AccordionItem>
      </div>
    </div>
  );
}
